import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d9u4h953pcjqs2fcsm20';

export default function NewsletterCTA() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setStatus('success');
      setEmail('');
      return;
    }

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim() }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (msg.toLowerCase().includes('spam')) {
          setStatus('error');
          setErrorMsg('提交失败，请稍后重试。');
        } else {
          setStatus('error');
          setErrorMsg(msg);
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请稍后重试。');
    }
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20gradient%20background%20with%20soft%20flowing%20silk%20fabric%20texture%2C%20ethereal%20light%20beams%2C%20minimalist%20elegant%20composition%2C%20warm%20amber%20cream%20and%20soft%20brown%20tones%2C%20spiritual%20and%20peaceful%20atmosphere&width=1600&height=600&seq=newsletter-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-950/50" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
          {t('section_newsletter')}
        </h2>
        <p className="text-background-50/80 text-sm mb-6">
          {t('section_newsletter_desc')}
        </p>

        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form="">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status !== 'idle') setStatus('idle');
            }}
            placeholder={t('newsletter_placeholder')}
            required
            className="flex-1 px-4 py-2.5 rounded-full bg-background-50/95 text-foreground-900 text-sm placeholder:text-foreground-400 border border-background-50/30 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            readOnly
            aria-hidden="true"
            className="honeypot-field"
          />
          <button
            type="submit"
            className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('newsletter_button')}
          </button>
        </form>

        {status === 'success' && (
          <p className="mt-4 text-sm text-green-300">{t('newsletter_success')}</p>
        )}
        {status === 'error' && (
          <p className="mt-4 text-sm text-red-300">{errorMsg}</p>
        )}
      </div>
    </section>
  );
}